import "./global.css";

import { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Box, Typography, Button, CssBaseline } from "@mui/material";
import { MsalAuthenticationTemplate, useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import { getAuthenticatedClient } from "./functions/graphClient";
import { callAPI } from "./functions/api";
import { useUser } from "./functions/userContext";
import { Loader } from "./components/shared/loader/loader";
import { AppThemeProvider } from "./components/themes/appThemeProvider";
import { CustomAppBar } from "./components/shared/appBar/appBar";

import styles from "./App.module.css";
import JiraView from "./views/jira/jiraView";
import CustomView from "./views/custom/customView";
import LockIcon from "@mui/icons-material/Lock";
import SideNav from "./components/shared/sideNav/sideNav";
import Ec2View from "./views/assets/ec2View";
import TenableView from "./views/security/tenableView";
import ManualUpdate from "./views/admin/manualUpdates";
import SettingsView from "./views/admin/settings";
import MonitoringView from "./views/monitoring/monitoringView";
import CostsView from "./views/costs/costView";
import MDRReportsView from "./views/reports/mdrReportsView";
import WeeklyReportsView from "./views/reports/weeklyReportsView";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ReportBuilder } from "./views/reports/reportBuilder/reportBuilder";
import { setAuthenticatedUser, reactPlugin } from "./functions/telemetry";
import { AppInsightsContext } from "@microsoft/applicationinsights-react-js";

function App() {
  const isAuthenticated = useIsAuthenticated();
  const [isPreloading, setIsPreloading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenu = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  useEffect(() => {
    const refreshInterval = setInterval(() => window.location.reload(), 5000000);
    return () => clearInterval(refreshInterval);
  }, []);

  useEffect(() => {
    const preload = async () => {
      if (!isAuthenticated) return;

      setIsPreloading(false);
    };

    preload();
  }, [isAuthenticated]);

  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={{ scopes: ["User.Read"] }}
    >
      {isAuthenticated ? (
        isPreloading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#303030",
              flex: 1,
              color: "#fff",
              textAlign: "center",
              borderRadius: "8px",
            }}
          >
            <Loader message="loading" />
          </Box>
        ) : (
          <AppInsightsContext.Provider value={reactPlugin}>
          <AppThemeProvider>
            <Router>
              <Box sx={{ display: "flex", backgroundColor: "background.default" }}>
                <CssBaseline />
                <SideNav />
                <MainContent
                  handleMenu={handleMenu}
                  anchorEl={anchorEl}
                  handleClose={handleClose}
                />
                <ToastContainer position="top-right" autoClose={5000} newestOnTop />
              </Box>
            </Router>
          </AppThemeProvider>
        </AppInsightsContext.Provider>
        )
      ) : (
        <Loader message="Authenticating" />
      )}
    </MsalAuthenticationTemplate>
  );
}

const MainContent = ({ drawerOpen, handleMenu, anchorEl, handleClose, toggleDrawer }) => {
  const handleLogout = () => {
    instance.logoutRedirect();
  };

  const handleSwitchAccount = () => {
    instance.loginRedirect({ prompt: "select_account" });
  };

  const { instance, accounts } = useMsal();
  const { user, setUser, setUserRoles, userLoading, setUserLoading, checkAccess, ROLE_CONFIG } =
    useUser();

  useEffect(() => {
    if (window.location.hostname === "localhost") return;

    // Check if Static Web App session exists
    fetch("/.auth/me", { credentials: "include" })
      .then((res) => res.json())
      .then((data) => {
        if (!data.clientPrincipal) {
          // Not logged in to SWA — redirect to login
          //window.location.href = "/.auth/login/aad";
        }
      })
      .catch((err) => {
        console.error("Error checking SWA auth:", err);
      });
  }, []);

  useEffect(() => {
    const setupUser = async () => {
      if (!accounts.length) return;

      const client = getAuthenticatedClient(accounts[0], instance);
      setUserLoading(true);
      try {
        const userProfile = await client.api("/me").get();
        setUser(userProfile);
        setAuthenticatedUser(userProfile.mail || userProfile.userPrincipalName, userProfile.displayName);
        const response = await callAPI("me/roles", "GET");
        setUserRoles(Array.isArray(response?.roles) ? response.roles : []);
      } catch (err) {
        console.error("Error fetching user info:", err);
      }

      setUserLoading(false);
    };

    setupUser();
  }, [accounts, instance]);

  const AccessRestricted = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        backgroundColor: "#303030",
        color: "#fff",
        textAlign: "center",
        padding: "20px",
        borderRadius: "8px",
      }}
    >
      <LockIcon sx={{ fontSize: 80, color: "#F9CA00", mb: 2 }} />
      <Typography variant="h5" sx={{ fontWeight: "bold" }}>
        {user ? "Access Denied" : "Authenticating..."}
        <Typography variant="body1" sx={{ mt: 1, maxWidth: "400px" }}>
          {user
            ? `Your account ${user.mail} doesn't have access.`
            : "We are logging you in. Please wait..."}
        </Typography>
      </Typography>
      <Button
        variant="contained"
        sx={{
          mt: 3,
          backgroundColor: "#F9CA00",
          color: "#303030",
          fontWeight: "bold",
          "&:hover": { backgroundColor: "#E0B200" },
        }}
        href="http://ask.cloudops.notified.com/"
        target="_blank"
      >
        Go to CloudOps JSM
      </Button>
    </Box>
  );

  return (
    <>
      <CustomAppBar />
      <Box
        component="main"
        className={`${styles.mainContent} custom-scrollbar`}
        sx={{
          flexGrow: 1,
          p: 0,
          m: 0,
          backgroundColor: "background.default",
          color: "text.primary",
          minHeight: "100vh",
          paddingTop: "64px",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        <Routes>
          <Route
            path="/"
            element={
              checkAccess([...ROLE_CONFIG.admin, ...ROLE_CONFIG.user, ...ROLE_CONFIG.finance, ...ROLE_CONFIG.editor, ...ROLE_CONFIG.manager]) ? (
                <CustomView />
              ) : userLoading ? (
                <Loader message="Authenticating..." />
              ) : (
                <AccessRestricted />
              )
            }
          />
          <Route
            path="/assets/ec2View"
            element={
              checkAccess([...ROLE_CONFIG.admin, ...ROLE_CONFIG.user, ...ROLE_CONFIG.finance, ...ROLE_CONFIG.editor, ...ROLE_CONFIG.manager]) ? (
                <Ec2View />
              ) : userLoading ? (
                <Loader message="Authenticating..." />
              ) : (
                <AccessRestricted />
              )
            }
          />
          <Route
            path="/security/tenable"
            element={
              checkAccess([...ROLE_CONFIG.admin, ...ROLE_CONFIG.manager]) ? (
                <TenableView />
              ) : userLoading ? (
                <Loader message="Authenticating..." />
              ) : (
                <AccessRestricted />
              )
            }
          />

          <Route
            path="/jira"
            element={
              checkAccess([...ROLE_CONFIG.admin, ...ROLE_CONFIG.manager]) ? (
                <JiraView />
              ) : userLoading ? (
                <Loader message="Authenticating..." />
              ) : (
                <AccessRestricted />
              )
            }
          />
          <Route
            path="/admin/manualUpdate"
            element={
              checkAccess([...ROLE_CONFIG.admin, ...ROLE_CONFIG.editor]) ? (
                <ManualUpdate />
              ) : userLoading ? (
                <Loader message="Authenticating..." />
              ) : (
                <AccessRestricted />
              )
            }
          />
          <Route
            path="/admin/settings"
            element={
              checkAccess([...ROLE_CONFIG.admin, ...ROLE_CONFIG.editor]) ? (
                <SettingsView />
              ) : userLoading ? (
                <Loader message="Authenticating..." />
              ) : (
                <AccessRestricted />
              )
            }
          />
          <Route
            path="/costs"
            element={
              checkAccess([...ROLE_CONFIG.admin, ...ROLE_CONFIG.finance]) ? (
                <CostsView />
              ) : userLoading ? (
                <Loader message="Authenticating..." />
              ) : (
                <AccessRestricted />
              )
            }
          />
          <Route
            path="/monitoring"
            element={
              checkAccess([...ROLE_CONFIG.admin, ...ROLE_CONFIG.user, ...ROLE_CONFIG.editor, ...ROLE_CONFIG.manager]) ? (
                <MonitoringView />
              ) : userLoading ? (
                <Loader message="Authenticating..." />
              ) : (
                <AccessRestricted />
              )
            }
          />
          <Route
            path="/reports/mdrReports"
            element={
              checkAccess([...ROLE_CONFIG.admin]) ? (
                <MDRReportsView />
              ) : userLoading ? (
                <Loader message="Authenticating..." />
              ) : (
                <AccessRestricted />
              )
            }
          />
          <Route
            path="/reports/weeklyReports"
            element={
              checkAccess([...ROLE_CONFIG.admin]) ? (
                <WeeklyReportsView />
              ) : userLoading ? (
                <Loader message="Authenticating..." />
              ) : (
                <AccessRestricted />
              )
            }
          />
          <Route
            path="/reports/reportBuilder"
            element={
              checkAccess([...ROLE_CONFIG.admin]) ? (
                <ReportBuilder />
              ) : userLoading ? (
                <Loader message="Authenticating..." />
              ) : (
                <AccessRestricted />
              )
            }
          />
        </Routes>
      </Box>
    </>
  );
};

export default App;
